<template>
  <div
    v-b-modal="`data-enabled-tag__modal--${tag.name}`"
    class="data-enabled-tag"
    :style="{ 'background-color': tagColor, color: tagTextColor, border: tagBorderColor }"
  >
    <span>
      {{ tag.name }}
      <b-icon v-if="isTagSelected(tag)" icon="record-fill" class="data-enabled-tag-indicator" />
    </span>

    <b-modal :id="modalId" hide-header-close :hide-footer="!tagHasValues" data-test-id="data-enabled-tag-modal">
      <template #modal-header>
        <strong>{{ tag.name }}</strong>

        <b-button size="sm" class="modal__close-button" data-test-id="data-enabled-tag-modal-cancel" @click="onClose">
          Cancel
        </b-button>
      </template>

      <b-form @submit.prevent="">
        <b-form-group>
          <div v-for="data in tag.data" :key="data.key">
            <DataEnabledNumberInput
              v-if="data.type === 'number'"
              v-model="formValues[data.key]"
              :label="data.name"
              class="modal__field"
            />

            <DataEnabledTextInput
              v-if="data.type === 'text'"
              v-model="formValues[data.key]"
              :label="data.name"
              class="modal__field"
            />

            <DataEnabledDateInput
              v-if="data.type === 'date'"
              v-model="formValues[data.key]"
              :label="data.name"
              class="modal__field"
            />

            <DataEnabledDateTimeInput
              v-if="data.type === 'datetime'"
              v-model="formValues[data.key]"
              :label="data.name"
              class="modal__field"
            />
          </div>

          <b-button block class="modal__add-button" :disabled="isFormEmpty" @click="onAdd">ADD</b-button>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <div v-for="value in tagValues" :key="value.id" class="modal-footer__history-value">
          <span class="modal-footer__formatted-string">{{ formatToString(value) }}</span>
          <b-icon icon="record-fill" class="modal-footer__separator" :color="tagTextColor" />
          <span class="modal-footer__updated-at">{{ formatToDateString(value.updatedAt) }}</span>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";

import { CaseEvent, CaseUpdateEventType } from "@/Case/CaseEvent";
import { getContrastColorFor } from "@/Case/services/ColorContrast";
import DataEnabledNumberInput from "@/Case/components/DataEnabledNumberInput.vue";
import DataEnabledTextInput from "@/Case/components/DataEnabledTextInput.vue";
import DataEnabledDateInput from "@/Case/components/DataEnabledDateInput.vue";
import DataEnabledDateTimeInput from "@/Case/components/DataEnabledDateTimeInput.vue";
import { format } from "date-fns-tz";

export default Vue.extend({
  name: "DataEnabledTag",

  components: {
    DataEnabledNumberInput,
    DataEnabledTextInput,
    DataEnabledDateInput,
    DataEnabledDateTimeInput,
  },

  props: {
    tag: {
      type: Object,
      required: true,
    },
    color: {
      type: String,
      required: true,
    },
    tagsSelected: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      tagColor: "",
      tagTextColor: "",
      formValues: {},
      modalId: `data-enabled-tag__modal--${this.tag.name}`,
    };
  },

  computed: {
    isFormEmpty() {
      return Object.values(this.formValues).filter((value) => !!value).length === 0;
    },

    tagBorderColor() {
      return this.isTagSelected(this.tag) ? `4px solid #ffffff` : `4px solid ${this.color}`;
    },

    tagValues() {
      return this.getTagValues(this.tag);
    },

    tagHasValues() {
      return this.tagValues.length !== 0;
    },
  },

  watch: {
    tagsSelected() {
      this.setTagColors();
    },
  },

  created() {
    this.setTagColors();
  },

  methods: {
    setTagColors() {
      this.tagColor = this.getTagColor();
      this.tagTextColor = getContrastColorFor(this.tagColor);
    },

    getTagColor() {
      return this.isTagSelected(this.tag) ? this.color : "#FFFFFF";
    },

    isTagSelected(tag) {
      return this.tagsSelected.some((item) => item.id === tag.id);
    },

    getTagValues(tag) {
      const tagSelected = this.tagsSelected.find((item) => item.id === tag.id) || {};
      return tagSelected.values || [];
    },

    formatToDateString(str) {
      return format(new Date(str), "hh:mm a, MM/dd/yyyy");
    },

    formatTagValueString(field) {
      return [field.name, field.value].join(" ");
    },

    formatToString(value) {
      const dateTime = value.fields.find((item) => item.type === "datetime");

      const formattedString =
        dateTime !== undefined
          ? format(new Date(dateTime.value), "E MMM dd - h:mm a ")
          : value.fields.map(this.formatTagValueString).join(", ");

      return formattedString;
    },

    onClose() {
      this.$bvModal.hide(this.modalId);
      this.resetFormValues();
    },

    onAdd() {
      const { tag, formValues, transformFormValues } = this;
      const fields = transformFormValues(formValues);

      this.$emit(CaseEvent.MODIFY, {
        eventType: CaseUpdateEventType.dataEnabledTag,
        data: { tag, fields },
      });

      this.$bvModal.hide(this.modalId);
      this.resetFormValues();
    },

    transformFormValues() {
      return Object.keys(this.formValues).map((key) => {
        const spec = this.tag.data.find((item) => item.key === key) || {};
        const value = this.formValues[key];

        return {
          key: key,
          value: value,
          name: spec.name,
          type: spec.type,
        };
      });
    },

    resetFormValues() {
      this.formValues = {};
    },
  },
});
</script>

<style scoped>
.data-enabled-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  word-break: break-all;
  font-weight: bold;
  background-color: #ffffff;
  color: #000;
  user-select: none;
}

.data-enabled-tag-indicator {
  width: 0.75rem;
  color: #ffffff;
  margin-left: -0.125rem;
}

.modal__close-button {
  background-color: transparent;
  color: #ff0000;
  border: none;
}

.modal__add-button {
  background-color: #cc0001;
  margin-top: 1rem;
}

.modal__field {
  margin-bottom: 0.75rem;
}

.modal-footer__history-value {
  display: grid;
  grid-template-columns: 9fr 1fr 9fr;
  width: calc(100% - 8px);
  align-items: center;
}

.modal-footer__formatted-string {
  justify-self: flex-start;
}

.modal-footer__separator {
  width: 0.5rem;
  color: #999999;
  justify-self: center;
}

.modal-footer__updated-at {
  color: #999999;
  white-space: nowrap;
  justify-self: flex-end;
}

.form-group {
  margin: 0;
}
</style>

<style>
.modal-content {
  padding-bottom: 1rem;
}

.modal-footer {
  max-height: 7.75rem;
  overflow-y: auto;
}

.modal-footer::-webkit-scrollbar {
  width: 6px;
}

.modal-footer::-webkit-scrollbar-thumb {
  background-color: #fff;
  border-radius: 16px;
}

.modal-footer::-webkit-scrollbar-button {
  display: none;
}
</style>
